import { Button, Grid, TextField, MenuItem } from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { IExecutiveCategory } from './types';

type EditExecutiveCategoryProps = {
  handleClose: () => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
  category: IExecutiveCategory | null;
};

type FormValues = {
  name: string;
  status: number | string;
};

function EditExecutiveCategory({
  handleClose,
  setRefreshTrigger,
  category,
}: EditExecutiveCategoryProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: category?.name || '',
      status: category?.status ?? 1,
    },
  });

  const [loading, setLoading] = useState<boolean>(false);
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  const onSubmit = async (values: FormValues) => {
    if (!category?.id) return;

    // ✅ 1) Check if anything actually changed
    const trimmedNewName = values.name.trim();
    const trimmedOldName = (category.name || '').trim();
    const newStatus = Number(values.status);
    const oldStatus = Number(category.status);

    const noChanges =
      trimmedNewName === trimmedOldName && newStatus === oldStatus;

    if (noChanges) {
      Toast.fire({
        icon: 'info',
        title: 'No changes detected to update.',
      });
      return;
    }

    try {
      if (!token) {
        Toast.fire({
          icon: 'error',
          title: 'Authentication token is missing. Please log in again.',
        });
        return;
      }

      setLoading(true);

      const payload = {};

      if (values.name !== category.name) {
        payload['name'] = values.name;
      }
      if (values.status !== category.status) {
        payload['status'] = values.status;
      }

      const { data } = await axios.patch(
        `${url}/api/v1/admin/executive/categories/${category.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: `${data.message}`,
        });
        setRefreshTrigger((prev: number) => prev + 1);
        reset();
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: `${data.message}`,
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: `${err?.response?.data?.message || 'Something went wrong'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Grid container px={1} spacing={1}>
        <Grid item xs={12}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='name'
            label='Category Name'
            type='text'
            autoFocus
            variant='outlined'
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', { required: 'Category name is required' })}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            select
            margin='normal'
            fullWidth
            id='status'
            label='Status'
            variant='outlined'
            defaultValue={category?.status ?? 1}
            error={!!errors.status}
            helperText={errors.status?.message}
            {...register('status', { required: 'Status is required' })}
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default EditExecutiveCategory;
